import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$, daysDiff, Modal, useForm, today } from '../components/helpers';

const STATUSES = ['Open', 'Invoiced', 'Paid', 'Overdue'];
const TERMS = ['Net 30', 'Net 45', 'Net 60', 'Due on Receipt', 'COD'];
const EMPTY = { order_number: '', customer: '', sku: '', finished_good_id: '', qty: '', amount: '', terms: 'Net 45', due_date: '', status: 'Open', special_instructions: '' };

const COMPANY = {
  name: 'PAK 1 PLASTICS',
  address: '401 E THOMAS ST, WILKES BARRE, PA 18705',
  phone: '917-544-3581',
};

function PrintModal({ orders, skuMap, supplierMap, onClose }) {
  const [copied, setCopied] = useState(false);

  // Group orders by supplier_id
  const grouped = {};
  orders.forEach(o => {
    const sku = skuMap[o.finished_good_id] || {};
    const supplierId = sku.supplier_id || 'unknown';
    const supplierName = supplierMap[supplierId] || sku.supplier || 'Unknown Supplier';
    if (!grouped[supplierName]) grouped[supplierName] = [];
    grouped[supplierName].push({ ...o, skuData: sku });
  });

  const printDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

  function generateEmailText() {
    const lines = [];
    lines.push('PRODUCTION ORDER');
    lines.push(COMPANY.name + ' · ' + COMPANY.address);
    lines.push('Tel: ' + COMPANY.phone);
    lines.push('Date: ' + printDate);
    lines.push('');

    Object.entries(grouped).forEach(([supplier, supplierOrders]) => {
      lines.push('TO: ' + supplier);
      lines.push('─'.repeat(60));
      lines.push(
        'ORDER #'.padEnd(14) +
        'SKU'.padEnd(16) +
        'PRODUCT'.padEnd(24) +
        'CUSTOMER'.padEnd(20) +
        'QTY'.padStart(8) +
        '  UPC'.padEnd(16) +
        'LABEL'.padEnd(8) +
        'DELIVERY'
      );
      lines.push('─'.repeat(60));

      supplierOrders.forEach(o => {
        lines.push(
          (o.order_number || '—').padEnd(14) +
          (o.sku || o.skuData?.sku || '—').padEnd(16) +
          (o.skuData?.name || '—').padEnd(24) +
          (o.customer || '—').padEnd(20) +
          String(Number(o.qty || 0).toLocaleString()).padStart(8) +
          ('  ' + (o.skuData?.upc || '—')).padEnd(16) +
          (o.skuData?.upc ? 'YES' : 'NO').padEnd(8) +
          (o.due_date || '—')
        );
      });

      lines.push('─'.repeat(60));
      lines.push('TOTAL: ' + supplierOrders.reduce((s, o) => s + Number(o.qty || 0), 0).toLocaleString() + ' units');

      const withInstructions = supplierOrders.filter(o => o.special_instructions);
      if (withInstructions.length > 0) {
        lines.push('');
        lines.push('SPECIAL INSTRUCTIONS:');
        withInstructions.forEach(o => {
          lines.push('  ' + o.order_number + ': ' + o.special_instructions);
        });
      }
      lines.push('');
    });

    lines.push('Please verify order received.');
    return lines.join('\n');
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(generateEmailText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  function doPrint() { window.print(); }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', color: '#000', borderRadius: 10, width: '100%', maxWidth: 760, fontFamily: 'monospace', fontSize: 13 }}>

        {/* Screen controls — hidden when printing */}
        <div className="no-print" style={{ padding: '14px 20px', borderBottom: '1px solid #ddd', display: 'flex', gap: 10, alignItems: 'center', background: '#f9f9f9', borderRadius: '10px 10px 0 0' }}>
          <button onClick={doPrint} style={{ padding: '7px 16px', background: '#1a4f8a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Print / Save as PDF</button>
          <button onClick={copyToClipboard} style={{ padding: '7px 16px', background: copied ? '#166534' : '#fff', color: copied ? '#fff' : '#333', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13, transition: 'all .2s' }}>
            {copied ? '✓ Copied!' : '📋 Copy for Email'}
          </button>
          <button onClick={onClose} style={{ padding: '7px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Close</button>
          <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>{orders.length} order{orders.length !== 1 ? 's' : ''} · {Object.keys(grouped).length} supplier{Object.keys(grouped).length !== 1 ? 's' : ''}</span>
        </div>

        {/* Printable content */}
        <div style={{ padding: '32px 40px' }} id="print-content">

          {Object.entries(grouped).map(([supplier, supplierOrders], groupIdx) => (
            <div key={supplier} style={{ marginBottom: groupIdx < Object.keys(grouped).length - 1 ? 48 : 0 }}>

              {/* Header */}
              <div style={{ borderBottom: '2px solid #000', paddingBottom: 12, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>PRODUCTION ORDER</div>
                    <div style={{ fontSize: 12, color: '#444' }}>{COMPANY.name} · {COMPANY.address}</div>
                    <div style={{ fontSize: 12, color: '#444' }}>Tel: {COMPANY.phone}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: '#444' }}>Date: {printDate}</div>
                    <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>To: <strong>{supplier}</strong></div>
                  </div>
                </div>
              </div>

              {/* Line items */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #000' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Order #</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>SKU</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Product</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Customer</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Qty</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>UPC</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Label</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierOrders.map((o, i) => (
                    <tr key={o.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                      <td style={{ padding: '8px 8px', fontSize: 12, color: '#555' }}>{o.order_number || '—'}</td>
                      <td style={{ padding: '8px 8px', fontWeight: 600 }}>{o.sku || o.skuData?.sku || '—'}</td>
                      <td style={{ padding: '8px 8px' }}>{o.skuData?.name || '—'}</td>
                      <td style={{ padding: '8px 8px' }}>{o.customer}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>{Number(o.qty || 0).toLocaleString()}</td>
                      <td style={{ padding: '8px 8px', fontSize: 12 }}>{o.skuData?.upc || '—'}</td>
                      <td style={{ padding: '8px 8px', fontSize: 12 }}>{o.skuData?.upc ? 'YES' : 'NO'}</td>
                      <td style={{ padding: '8px 8px', fontSize: 12 }}>{o.due_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #000' }}>
                    <td colSpan={4} style={{ padding: '8px 8px', fontWeight: 700, fontSize: 12 }}>TOTAL</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700 }}>
                      {supplierOrders.reduce((s, o) => s + Number(o.qty || 0), 0).toLocaleString()}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>

              {/* Special instructions per order */}
              {supplierOrders.some(o => o.special_instructions) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, borderBottom: '1px solid #ccc', paddingBottom: 4 }}>Special Instructions</div>
                  {supplierOrders.filter(o => o.special_instructions).map(o => (
                    <div key={o.id} style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>{o.order_number}</strong> — {o.special_instructions}
                    </div>
                  ))}
                </div>
              )}

              {/* Signature line */}
              <div style={{ marginTop: 32, display: 'flex', gap: 40 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ borderTop: '1px solid #000', paddingTop: 6, fontSize: 11, color: '#666' }}>Authorized by</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ borderTop: '1px solid #000', paddingTop: 6, fontSize: 11, color: '#666' }}>Date</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ borderTop: '1px solid #000', paddingTop: 6, fontSize: 11, color: '#666' }}>Supplier acknowledgment</div>
                </div>
              </div>

              {/* Page break between suppliers */}
              {groupIdx < Object.keys(grouped).length - 1 && (
                <div style={{ pageBreakAfter: 'always', marginTop: 40 }} />
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body * { visibility: hidden; }
          #print-content, #print-content * { visibility: visible; }
          #print-content { position: fixed; top: 0; left: 0; width: 100%; padding: 32px 40px; font-family: monospace; font-size: 13px; color: #000; background: #fff; }
        }
      `}</style>
    </div>
  );
}

export default function CustomerOrders() {
  const [items, setItems] = useState([]);
  const [skus, setSkus] = useState([]);
  const [suppliersData, setSuppliersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, set, setForm] = useForm(EMPTY);
  const [selected, setSelected] = useState(new Set());
  const [showPrint, setShowPrint] = useState(false);

  async function load() {
    const [{ data: ar }, { data: fg }, { data: sup }] = await Promise.all([
      supabase.from('customer_orders').select('*').order('created_at', { ascending: false }),
      supabase.from('finished_goods').select('id,sku,name,unit_price,supplier_id,upc').order('sku'),
      supabase.from('suppliers').select('id,name').order('name'),
    ]);
    setItems(ar || []);
    setSkus(fg || []);
    setSuppliersData(sup || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // SKU map for quick lookup
  const skuMap = skus.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
  const supplierMap = suppliersData.reduce((acc, s) => { acc[s.id] = s.name; return acc; }, {});

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.id)));
    }
  }

  function openAdd() { setForm(EMPTY); setEditing(null); setModal('form'); }
  function openEdit(item) {
    setForm({
      order_number: item.order_number, customer: item.customer,
      sku: item.sku || '', finished_good_id: item.finished_good_id || '',
      qty: item.qty || '', amount: item.amount, terms: item.terms,
      due_date: item.due_date || '', status: item.status,
      special_instructions: item.special_instructions || '',
    });
    setEditing(item); setModal('form');
  }

  function onSkuChange(e) {
    const id = e.target.value;
    const sku = skus.find(s => s.id === id);
    setForm(f => ({ ...f, finished_good_id: id, sku: sku ? sku.sku : f.sku, amount: (sku && f.qty && sku.unit_price) ? (Number(f.qty) * Number(sku.unit_price)).toFixed(2) : f.amount }));
  }

  function onQtyChange(e) {
    const qty = e.target.value;
    const sku = skus.find(s => s.id === form.finished_good_id);
    setForm(f => ({ ...f, qty, amount: (sku && sku.unit_price && qty) ? (Number(qty) * Number(sku.unit_price)).toFixed(2) : f.amount }));
  }

  async function save() {
    if (!form.order_number || !form.customer || !form.amount) {
      alert('Order #, customer, and amount are required.'); return;
    }
    const payload = {
      order_number: form.order_number.trim(), customer: form.customer.trim(),
      sku: form.sku.trim() || null, finished_good_id: form.finished_good_id || null,
      qty: Number(form.qty) || 0, amount: Number(form.amount),
      terms: form.terms, due_date: form.due_date || null, status: form.status,
      special_instructions: form.special_instructions.trim() || null,
    };
    if (editing) {
      await supabase.from('customer_orders').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('customer_orders').insert(payload);
    }
    setModal(null); load();
  }

  async function del(id) {
    if (!window.confirm('Delete this order?')) return;
    await supabase.from('customer_orders').delete().eq('id', id);
    load();
  }

  const statusBadge = { Open: 'badge-blue', Invoiced: 'badge-warn', Paid: 'badge-ok', Overdue: 'badge-danger' };
  const totalOpen = items.filter(o => o.status !== 'Paid').reduce((s, o) => s + Number(o.amount), 0);
  const overdueAR = items.filter(o => o.status !== 'Paid' && o.due_date && daysDiff(o.due_date) > 0);
  const selectedOrders = items.filter(i => selected.has(i.id));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Customer Orders &amp; AR</div>
          <div className="page-sub">
            Outstanding: <strong>{fmt$(totalOpen)}</strong>
            {overdueAR.length > 0 && <span className="text-danger"> · Overdue: {fmt$(overdueAR.reduce((s,o) => s+Number(o.amount),0))}</span>}
          </div>
        </div>
        <div className="flex-row">
          {selected.size > 0 && (
            <button className="btn btn-primary" onClick={() => setShowPrint(true)}>
              🖨 Production Order ({selected.size})
            </button>
          )}
          <button className="btn btn-primary" onClick={openAdd}>+ New Order</button>
        </div>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleSelectAll} />
              </th>
              <th>Order #</th><th>Customer</th><th>SKU</th><th>Qty</th><th>Amount</th><th>Terms</th><th>Due Date</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr className="empty-row"><td colSpan={10}>Loading...</td></tr>}
            {!loading && items.length === 0 && <tr className="empty-row"><td colSpan={10}>No customer orders yet.</td></tr>}
            {items.map(item => {
              const overdue = item.status !== 'Paid' && item.due_date && daysDiff(item.due_date) > 0;
              return (
                <tr key={item.id} className={overdue ? 'overdue-row' : ''}>
                  <td>
                    <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} />
                  </td>
                  <td className="mono">{item.order_number}</td>
                  <td>{item.customer}</td>
                  <td>{item.sku ? <span className="badge badge-blue mono">{item.sku}</span> : '—'}</td>
                  <td className="mono">{item.qty ? Number(item.qty).toLocaleString() : '—'}</td>
                  <td className="mono"><strong>{fmt$(item.amount)}</strong></td>
                  <td><span className="badge badge-gray">{item.terms}</span></td>
                  <td className={overdue ? 'text-danger' : 'text-muted'} style={{ fontSize: 12 }}>
                    {item.due_date || '—'}
                    {overdue && <div style={{ fontSize: 11 }}>{daysDiff(item.due_date)} days overdue</div>}
                  </td>
                  <td><span className={`badge ${statusBadge[item.status] || 'badge-gray'}`}>{item.status}</span></td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(item.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPrint && (
        <PrintModal
          orders={selectedOrders}
          skuMap={skuMap}
          supplierMap={supplierMap}
          onClose={() => setShowPrint(false)}
        />
      )}

      {modal === 'form' && (
        <Modal title={editing ? 'Edit Customer Order' : 'New Customer Order'} onClose={() => setModal(null)} onSave={save}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Order # *</label>
              <input className="form-input mono" value={form.order_number} onChange={set('order_number')} placeholder="e.g. SO-2026-001" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <input className="form-input" value={form.customer} onChange={set('customer')} placeholder="e.g. Sherwin-Williams" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">SKU</label>
              <select className="form-input" value={form.finished_good_id} onChange={onSkuChange}>
                <option value="">— Select SKU —</option>
                {skus.map(s => <option key={s.id} value={s.id}>{s.sku} – {s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Qty</label>
              <input className="form-input mono" type="number" min="0" value={form.qty} onChange={onQtyChange} placeholder="0" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Amount ($) *</label>
              <input className="form-input mono" type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" />
              <div className="form-hint">Auto-filled from SKU unit price × qty</div>
            </div>
            <div className="form-group">
              <label className="form-label">Terms</label>
              <select className="form-input" value={form.terms} onChange={set('terms')}>
                {TERMS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={form.due_date} onChange={set('due_date')} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status} onChange={set('status')}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Special Instructions</label>
            <input className="form-input" value={form.special_instructions} onChange={set('special_instructions')} placeholder="e.g. Black repro, no tab, bulk pack..." />
          </div>
        </Modal>
      )}
    </div>
  );
}
